import styled, { css } from 'styled-components'
import { Colors, Spacing } from 'cio-design-library/design-tokens'
import { Typography, FontWeights } from 'builder/styles/typography'
import Media from 'builder/styles/media'
import { IconBoxProps, BarProps } from './types'

export const Container = styled.div`
  ${Typography.S};
  ${FontWeights.Medium};
  text-align: center;
  position: relative;
  margin-bottom: 2px;
  padding: 0 32px;
`

export const Input = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  padding: 0;
  margin: 0;
  border: none;
  font: inherit;
  line-height: inherit;
  text-align: center;
  caret-color: ${Colors.Blue50};

  &::placeholder {
    color: ${Colors.Neutral50};
  }
`

export const IconBox = styled.div<IconBoxProps>(({ isEditIconVisible }) => {
  return css`
    color: ${Colors.Neutral40};
    position: absolute;
    left: 100%;
    margin-left: 2px;
    top: 50%;
    margin-top: -11px;
    width: 24px;
    height: 24px;
    visibility: hidden;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.3s ease, visibility 0.3s ease;

    ${Media.Hover`
    &:hover {
      color: ${Colors.Blue50};
      opacity: 1;
      visibility: visible;
    }

    ${Container}:hover & {
      visibility: visible;
      opacity: 1;
    }
  `};

    & svg {
      display: block;
    }

    ${isEditIconVisible &&
    css`
      opacity: 1;
      visibility: visible;
    `}
  `
})

export const Panel = styled.div`
  display: inline-block;
  position: relative;
  max-width: 100%;
  vertical-align: top;
`

export const TextBox = styled.div`
  display: inline-block;
  position: relative;
  overflow: hidden;
  max-width: 100%;
  height: 34px;
  vertical-align: top;
`

export const Text = styled.div`
  max-width: 100%;
  white-space: pre;
  min-width: 3.2em;
  display: inline-block;
  text-align: center;
  visibility: hidden;
  pointer-events: none;
`

export const Bar = styled.div<BarProps>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background-color: ${Colors.Blue50};
  pointer-events: none;
  transition: opacity 0.1s ease, transform 0.1s ease;

  ${props => {
    switch (props.in) {
      case 'entering':
        return css`
          opacity: 1;
          transform: scaleX(1);
        `
      case 'entered':
        return css`
          opacity: 1;
          transform: scaleX(1);
        `
      case 'exiting':
        return css`
          opacity: 0;
          transform: scaleX(1);
        `
      case 'exited':
        return css`
          opacity: 0;
          transform: scaleX(0.5);
        `
      default:
        return ''
    }
  }};
`

export const Pencil = styled.div<{ $icon: string }>`
  margin-left: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  background-color: ${Colors.Neutral40};
  mask: url(${props => props.$icon}) no-repeat center;
  mask-size: contain;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  opacity: 0;

  ${Media.Tablet`
    opacity: 1;
  `}
`

export const CompactContainer = styled.div<{ $isHovered: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  cursor: ${props => (props.$isHovered ? 'pointer' : 'default')};
  width: fit-content;

  &:hover ${Pencil} {
    background-color: ${Colors.Blue50};
    opacity: 1;
  }
`

export const CompactTextDisplay = styled.span<{ $isHovered: boolean }>`
  ${Typography.Body};
  color: ${props => (props.$isHovered ? Colors.Blue50 : Colors.Neutral90)};
  transition: color 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 2px solid transparent;
  width: fit-content;
  max-width: 335px;

  ${Media.Laptop`
    max-width: 190px;
  `}
`

export const CompactInput = styled.input`
  ${Typography.Body};
  color: ${Colors.Neutral90};
  border: none;
  border-bottom: 2px solid ${Colors.Blue50};
  background: transparent;
  outline: none;
  padding: 0 0 4px 0;
  margin: 0;
  max-width: 335px;
  caret-color: ${Colors.Blue50};

  &::placeholder {
    color: ${Colors.Neutral50};
  }

  ${Media.Laptop`
    max-width: 190px;
  `}
`

export const CompactTooltip = styled.div`
  position: absolute;
  bottom: -24px;
  right: -20px;
  padding: ${Spacing['3XS']} ${Spacing['2XS']};
  background: ${Colors.Neutral90};
  color: ${Colors.White};
  ${Typography.Tiny};
  border-radius: 4px;
  white-space: nowrap;
  z-index: 1000;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-bottom-color: ${Colors.Neutral90};
  }
`
